/**
 * The live stages, plus NOT_WON.
 *
 * The rule the model is built on, from LEAD_STATE_MACHINE.md:
 *
 *   A stage answers "who are we waiting on". It does not record "what have we done".
 *
 * Anything that lasts five minutes is an action, not a stage. That is why there is no
 * `Contacted`, no `Quote Drafted` and no `Negotiating` — each was rejected with a reason
 * recorded in the contract.
 */

/**
 * `NOT_WON`, not `LOST`. Renamed 2026-09-04 on the Product Owner's instruction:
 *
 *   叫 lose 或叫 fail 就会让人感觉不想往那边点…你就写 not win
 *
 * The risk a dramatic label creates is not a wording problem. It is leads that are never closed
 * at all, because nobody wants to be the person who marked one "Lost" -- so they sit in a live
 * stage for months, wrong in the daily queue and wrong in every funnel figure. Won / Not won
 * reads as a pair and costs an operator nothing to click.
 */
/**
 * `CHECKED_OUT` and `CANCELLED` were added 2026-10-15 (#554 step A) on the Product Owner's
 * decision, with nothing moved into them: 128 leads, 0 in either.
 *
 * They are the two words the channels have always had and this product has not. A booking that
 * ran its course and a booking that was called off both ended up at `WON` here — the word this
 * stage carried until step B renamed it — which made it answer two different questions at once:
 * "did we win it" and "is it still happening".
 *
 * They are NOT the same word in a different mood. Cancelled means the money and the nights went
 * away; Checked out means both happened and are finished. A funnel that cannot tell them apart
 * reports a cancelled stay as revenue for as long as nobody edits it by hand.
 */
/**
 * `INQUIRY` IS `NEW` AND `IN_CONVERSATION` MADE ONE STAGE, 2026-10-16 (#554 step C).
 *
 * The two were never a fact about the renter. The stage only moved from New to In conversation
 * when somebody pressed this product's own Send — so a reply Yuliana wrote in Quo, or on the
 * channel's own page, left the lead at New for ever. Measured on production 2026-10-15: of the 8
 * leads sitting at New, 7 had already been answered, two of them over weeks (Emily Longhurst, 48
 * messages in and 54 out). The screen said "nobody has replied · target 30 minutes" about a
 * conversation a month old.
 *
 * A STAGE THAT ONLY MOVES WHEN WE PRESS A BUTTON CANNOT TELL THE TRUTH ABOUT A CONVERSATION THAT
 * HAPPENS SOMEWHERE ELSE. So the question it was pretending to answer — has anyone replied to her
 * yet — is asked of the timeline instead, where the answer is a fact rather than a bookkeeping
 * step (`src/leads/stale.js`, `src/today/today.js`). The stage goes back to answering only what a
 * stage can: how far the deal has got.
 *
 * The Product Owner, 2026-10-15, in his own words: 「如果他丢我们，我们还没有回复的，我们就要回，
 * 不用看 stage。」
 */
export const STAGES = [
  'INQUIRY', 'QUOTE_SENT', 'CONTRACT_PENDING', 'CONFIRMED', 'CHECKED_OUT',
  'CANCELLED', 'NOT_WON',
]

/**
 * Order for the funnel. `CHECKED_OUT` is the step after `CONFIRMED` — the stay happened and ended.
 *
 * `CANCELLED` and `NOT_WON` are both terminal and both sit outside the chain, for the same
 * reason and with a difference worth keeping: NOT_WON never became a booking, CANCELLED was one
 * and stopped being one.
 */
export const FORWARD_CHAIN = [
  'INQUIRY', 'QUOTE_SENT', 'CONTRACT_PENDING', 'CONFIRMED', 'CHECKED_OUT',
]

/**
 * The stages where somebody is still waiting on somebody.
 *
 * These are what the Leads list shows by default. CONFIRMED and NOT_WON are settled: nothing about
 * them needs doing today, and leaving them in the default view means the list grows for ever
 * while the part that needs work stays the same size. Both remain one click away.
 */
export const ACTIVE_STAGES = ['INQUIRY', 'QUOTE_SENT', 'CONTRACT_PENDING']

/**
 * EVERY STAGE, IN THE ORDER A PERSON READS THEM: the forward chain, then the two that end a deal.
 *
 * Both the Inbox's stage rail and the Leads board draw one row or column per stage, and each used
 * to build its own list — the rail as `[...FORWARD_CHAIN, 'NOT_WON']`, the board as the same words
 * written out again. Two lists is how one of them comes to be missing a stage, which is the defect
 * `tests/stageVocabulary.test.mjs` exists to catch; this is the definition it checks against.
 *
 * `CANCELLED` sits before `NOT_WON` rather than after it because that is the order of how far a
 * deal got: a cancelled booking was once further along than one we never won.
 */
export const ALL_STAGES_IN_ORDER = [...FORWARD_CHAIN, 'CANCELLED', 'NOT_WON']

/**
 * WORDS THE DATABASE STILL ACCEPTS AND THIS PRODUCT NO LONGER WRITES.
 *
 * A rename cannot be atomic here: migrations are applied to production by hand and the code
 * deploys on merge, so for some minutes one half knows a word the other does not. The old word
 * stays legal for exactly that window — `src/data/map.js` turns every row that still carries one
 * into its new stage on the way in, and a follow-up migration drops it once the rows are gone.
 *
 * EVERY ENTRY HERE IS A DEBT WITH A NUMBER. `WON` → `CONFIRMED` lived here from 2026-10-15 until
 * migration 0098 moved the last 27 rows and dropped the word (#554 step B, MTR #571) — about four
 * hours, which is what a rename window should cost. A list that carries the same word for a month
 * means a migration was never applied, which is a thing this repository has done before (MTR #572).
 *
 * Step C's two entries lived here from 2026-10-16 until migration 0109 moved the last 50 leads
 * and 77 timeline entries and dropped both words — a few hours, which is what a rename window
 * should cost. THE MECHANISM STAYS AND THE LIST EMPTIES: `src/data/map.js` still reads this on
 * every row, so the next rename costs one line here rather than a new translation written again.
 *
 * WHAT DOES NOT LIVE HERE, and is the other half of a stage rename: the words a person SAVED into
 * a rule or a folder. Those are not translated by this map and never expire — a rule filtered on
 * "Lead status is one of New" still means what its author chose, and
 * `src/automation/filterUpgrade.js` keeps saying so for as long as the row exists.
 *
 * It is a map rather than a list, and it is what `src/data/map.js` reads, so the record of the
 * debt and the code that pays it cannot drift apart: a word added here is translated from that
 * moment, and a word removed here stops being translated.
 */
export const RETIRING_STAGES = {}

/**
 * THE STAGES THAT COUNT AS A DEAL WE WON, for Reports and for the conversion rate.
 *
 * Not the same question as "is this booking still happening", which is what the stage itself
 * answers. Three stages say yes to this one, for three different reasons:
 *
 *   CONFIRMED     the booking exists.
 *   CHECKED_OUT   it existed and the stay finished. A win that is over is still a win.
 *   CANCELLED     it converted and then came undone. The Product Owner's ruling, 2026-10-15:
 *                 「canceled 算成交吧」 — the renter's change of mind is not a sales outcome.
 *
 * IT IS A LIST RATHER THAN THREE `||`s BECAUSE THE SCREEN READS IT. The Won tile on Reports
 * prints these stages' own labels under its number, so the tile cannot claim to count something
 * the arithmetic does not — and the day a fourth stage joins, the tile says so without anybody
 * remembering to edit it. That is the Product Owner's ask, 2026-10-15: 把 Won 的定义写清楚，
 * 放成一个小的标签在旁边，这样我们一看就知道它包含了什么。
 */
export const COUNTS_AS_WON = ['CONFIRMED', 'CHECKED_OUT', 'CANCELLED']

export const isActive = (stage) => ACTIVE_STAGES.includes(stage)

/**
 * SETTLED: nothing more will happen to this lead without somebody deliberately reopening it.
 *
 * It lives here rather than in a screen because two screens now act on it. `LeadDetail` kept a
 * private copy to decide whether to offer the Scam Alert actions — `moveStage` refuses a move to
 * the stage a lead is already in, so those buttons on a settled lead threw, were swallowed, and
 * changed nothing (mtr#84) — and the Leads list needs the same answer for the same reason.
 *
 * Two copies of a rule is how the applies-to draft rule drifted between the two pricing screens
 * (mtr#102) and how the deposit policy came to decide nothing (mtr#115). One definition, both
 * callers.
 */
export const SETTLED_STAGES = new Set(['CONFIRMED', 'CHECKED_OUT', 'CANCELLED', 'NOT_WON'])

/**
 * THE NEXT STEP WRITTEN ON A LEAD, ONLY WHILE IT CAN STILL BE TAKEN.
 *
 * `next_step` is written when a lead arrives ("They said when and roughly how long. Check availability and
 * send options.") and nothing ever clears it. On a lead somebody has marked Not won, the Inbox kept
 * showing that amber NEXT STEP — telling the team to quote a renter they had just closed. A lead that
 * ended without a stay (Not won, Cancelled) has no next step to take; its stage line says where it
 * ended. A Confirmed guest's note ("Booked on … reservation 61W8600R1F") still stands, so it stays.
 */
const ENDED_WITHOUT_A_STAY = new Set(['NOT_WON', 'CANCELLED'])
export const standingNextStep = (lead) => {
  const said = typeof lead?.nextStep === 'string' ? lead.nextStep.trim() : ''
  return said && !ENDED_WITHOUT_A_STAY.has(lead?.stage) ? said : null
}

export const STAGE_LABELS = {
  INQUIRY: 'Inquiry',
  QUOTE_SENT: 'Quote Sent',
  CONTRACT_PENDING: 'Contract Pending',
  CONFIRMED: 'Confirmed',
  CHECKED_OUT: 'Checked out',
  CANCELLED: 'Cancelled',
  NOT_WON: 'Not won',
}

/** Who the lead is waiting on. Shown so the stage explains itself. */
export const WAITING_ON = {
  /* Either of us: the timeline says which, and no stage ever could — see the note on STAGES. */
  INQUIRY: 'mixed',
  QUOTE_SENT: 'renter',
  CONTRACT_PENDING: 'renter',
  CONFIRMED: null,
  CHECKED_OUT: null,
  CANCELLED: null,
  NOT_WON: null,
}

/**
 * Transitions that carry an evidence gate. Everything else is human judgement.
 *
 * `requires` is the evidence the caller must supply. The UI turns these into the action
 * that earns the move — dropping a card on `Quote Sent` opens the Quote Builder rather
 * than setting a field — but the rule lives here, not in a component.
 */
export const EVIDENCE_GATES = {
  QUOTE_SENT: {
    requires: ['sendEvidence'],
    describe: 'Provider send evidence, or a recorded manual-send entry. Pressing Send is not evidence.',
  },
  CONTRACT_PENDING: {
    requires: ['quoteVersion', 'quoteOption', 'availabilityRecheckedAt'],
    describe: 'The exact quote version and option, plus a successful availability recheck at the moment of confirmation.',
  },
  CONFIRMED: {
    requires: ['signatureRef', 'firstPaymentRef', 'reservationRef'],
    describe: 'Contract signature, first payment, and a verified Guesty reservation reference.',
  },
}

export function isStage(value) {
  return STAGES.includes(value)
}

/**
 * May a lead move from `from` to `to`?
 *
 * Forward moves may skip stages — a renter who supplies complete requirements in the first
 * message can go INQUIRY -> QUOTE_SENT. Backward moves are allowed with nothing attached: no
 * reason, no approval, no confirmation. The timeline already records who moved it and when.
 *
 * Returns { allowed, needsEvidence, needsReason, reason? }. It never throws for an ordinary
 * refusal — an unknown stage is a programming error and does throw.
 */
export function canMove(from, to) {
  if (!isStage(from)) throw new TypeError(`unknown stage: ${from}`)
  if (!isStage(to)) throw new TypeError(`unknown stage: ${to}`)

  if (from === to) {
    return { allowed: false, needsEvidence: null, needsReason: false, reason: 'already in this stage' }
  }
  if ((from === 'NOT_WON' || from === 'CANCELLED') && from !== to) {
    // Reopening is allowed and deliberately unceremonious: both are terminal for reporting,
    // not for people. A renter who cancels in March and books again in May is one renter, and
    // making somebody create a second lead for her is how a product loses her history.
    // Permissions: none.
    return { allowed: true, needsEvidence: null, needsReason: false }
  }
  if (to === 'NOT_WON') {
    return { allowed: true, needsEvidence: null, needsReason: true }
  }
  /*
    CANCELLED NEEDS NO REASON, AND NOT_WON DOES. That is not an inconsistency.

    A reason is demanded at NOT_WON because the answer is ours and nobody else will ever write it
    down — we are the only witness to why a deal we were in died. A cancellation is the renter's
    act, usually reaching us from the channel with the channel's own words attached, and a
    required box in front of it would be filled with "cancelled" by the second week.
  */
  return { allowed: true, needsEvidence: EVIDENCE_GATES[to] ?? null, needsReason: false }
}

/**
 * Apply a move, returning a new lead plus the timeline entry it earned.
 *
 * `at` is supplied by the caller and is never read from the clock in here. The watermark
 * lesson from owner-acquisition-engine: a time the system invents is a time nobody can
 * reconcile against what a person actually saw.
 */
export function moveStage(lead, to, { at, by, evidence = {}, notWonReason = null } = {}) {
  if (!at) throw new TypeError('moveStage requires `at` — never default to the clock')
  const verdict = canMove(lead.stage, to)
  if (!verdict.allowed) throw new Error(`cannot move ${lead.stage} -> ${to}: ${verdict.reason}`)

  if (verdict.needsEvidence) {
    /*
      `evidence?.[k]`, NOT `evidence[k]`.

      The default of `{}` only applies when the argument is absent. A caller passing an explicit
      `null` — which `attachQuote` does the moment its own caller has none — reached this line and
      threw "Cannot read properties of null (reading 'sendEvidence')": a sentence about our code,
      raised at a person in the middle of sending a quote, in place of the refusal this block
      exists to produce. No evidence is exactly the case being tested for; it is not a crash.
    */
    const missing = verdict.needsEvidence.requires.filter((k) => evidence?.[k] == null)
    if (missing.length) {
      throw new Error(
        `cannot move to ${to} without evidence: ${missing.join(', ')} — ${verdict.needsEvidence.describe}`
      )
    }
  }
  if (verdict.needsReason && !notWonReason) {
    throw new Error('cannot move to NOT_WON without a reason')
  }

  return {
    lead: {
      ...lead,
      stage: to,
      stageEnteredAt: at,
      notWonReason: to === 'NOT_WON' ? notWonReason : null,
      ...(verdict.needsEvidence ? { evidence: { ...lead.evidence, ...evidence } } : {}),
    },
    timelineEntry: {
      kind: 'stage_change',
      from: lead.stage,
      to,
      at,
      by,
      ...(notWonReason ? { notWonReason } : {}),
    },
  }
}
